import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { Box } from 'components/Grid';
import Image from 'components/Image';
import Video from 'components/Video';
import theme from 'utils/styles/theme';
import H2 from 'components/H2';
import Text from 'components/Text';

const ProjectGridItemStyled = styled(Box)`
	position: relative;

	&:before {
		content: '';
		height: 0;
		display: inline-block;
		padding-top: ${props => props.aspectRatio * 100}%;
		width: 1px;
		position: relative;
	}

	.ProjectGridItem-Inner {
		position: absolute;
		width: 100%;
		top: 0;
		left: 0;
		height: 100%;
		display: flex;

		img {
			margin: 0;
			position: absolute !important;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
		}

		.gatsby-image-wrapper {
			z-index: 1;
		}

		.tag {
			position: absolute;
		}
	}

	.ProjectGridItem-Content {
		display: flex;
		z-index: 1;
		width: 100%;
		overflow: hidden;

		* {
			z-index: 1;
		}

		h2 {
			margin: 0;
			transition: transform 0.4s ease-in-out;
		}

		&:before {
			content: '';
			display: block;
			position: absolute;
			width: 100%;
			top: 0;
			left: 0;
			height: 100%;
			border: solid 10px transparent;
			-webkit-appearance: none;
			-moz-appearance: none;
		}
	}

	&:hover {
		.ProjectGridItem-Content:before {
			border: solid 10px ${theme.colors.client.fontColor};
		}
	}
`;

const ProjectGridItem = ({ item, ...props }) => {
	const mode = !item.featuredImage || item.darkText ? 'dark' : 'light';

	let formattedType = '';
	let itemPath = '';

	switch (item.type) {
		case 'ContentfulCase':
			formattedType = 'Case';
			itemPath = `/case/${item.slug}`;
			break;
		case 'ContentfulPost':
			formattedType = 'Nyhet';
			itemPath = `/nyheter/${item.slug}`;
			break;
		default:
			break;
	}

	const renderMedia = () => {
		if (item.featuredImage.file.contentType === 'video/mp4') {
			return (
				<Video background={true} autoPlay muted loop playsInline src={item.featuredImage.file.url}>
					<video autoPlay muted loop playsInline src={item.featuredImage.file.url} />
				</Video>
			);
		} else {
			return (
				<Image
					style={{
						position: 'absolute'
					}}
					height={['100%']}
					fluid={item.featuredImage.fluid}
				/>
			);
		}
	};
	return (
		<ProjectGridItemStyled
			{...props}
			as="a"
			href={itemPath}
			bg="client.accent"
			color={mode === 'dark' ? 'client.fontColor' : 'white'}
			cursor={mode === 'dark' ? 'pointer' : 'pointerWhite'}
		>
			{item && (
				<div className="ProjectGridItem-Inner">
					{item.featuredImage && renderMedia()}

					<Box
						alignItems="flex-end"
						p={[3, 3, 4]}
						pr={[3, 3, 6]}
						className="ProjectGridItem-Content"
					>
						<H2 fontSize={['h3', '3.5vw', '3.5vw', '40px']}>{item.headline}</H2>
						<Text
							color={mode === 'dark' ? 'client.primary' : 'white'}
							top={[16, 16, 32]}
							left={[16, 16, 32]}
							className="tag"
							uppercase
							fontSize={['12px']}
						>
							{formattedType}
						</Text>
					</Box>
				</div>
			)}
		</ProjectGridItemStyled>
	);
};

ProjectGridItem.propTypes = {
	siteTitle: PropTypes.string,
	aspectRatio: PropTypes.number,
	type: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	item: PropTypes.shape({
		headline: PropTypes.string,
		slug: PropTypes.string,
		title: PropTypes.string,
		darkText: PropTypes.bool
	})
};

ProjectGridItem.defaultProps = {
	mode: 'dark',
	aspectRatio: 1,
	type: 'ContentfulCase',
	height: ['400px', 'auto'],
	item: {
		headline: '',
		title: '',
		slug: ''
	}
};

export default ProjectGridItem;
